import * as React from 'react';
import { ErrorBoundary } from '../errors/ErrorBoundary';
import { ActionSelection } from './DeviceInteractionModal/ActionSelection';
import { SessionSelection } from './DeviceInteractionModal/SessionSelection';
import { Upload } from './DeviceInteractionModal/Upload';
import { Success } from './DeviceInteractionModal/Success';
import { Failure } from './DeviceInteractionModal/Failure';
import { AuditModalBody } from '../auditmodal/components/AuditModalBody';
import { MailConfirmation } from './DeviceInteractionModal/Mail/MailConfirmation';
import { DeviceUnresponsive } from './DeviceInteractionModal/DeviceUnresponsiveInstruction';
import { Device } from '../misc/api/DeviceLinkApi/Devices';
import { DeviceTypes } from '../misc/api/apiTypes';
import { CloseInteractionModalAction, DevicesAction, IsFetchingAction, SetInteractionModalStepAction } from './DevicesNewReducer';
import { useDevicesStateContext } from './DevicesStateContext';
import { useWebPortalLegacyClient, useWebPortalApiClient } from '../misc/api/WebPortalTokenClient';
import { ResetSelection } from './DeviceInteractionModal/ResetSelection';
import { DiaryResetSubjectConfiguration } from './DeviceInteractionModal/DiaryResetSubjectConfiguration';
import { DiaryLaunchCalibrationVerification } from './DeviceInteractionModal/DiaryLaunchCalibrationVerification';

export interface DeviceInteractionModalProps {
    dispatch: React.Dispatch<DevicesAction>;
}

export const DeviceInteractionModal = ({ dispatch }: DeviceInteractionModalProps) => {
    const { upload: { activeSelectedSessionId, deviceStatus, selectedSessionIds }, mail: { isFetchingAddress, mailAddress }, interactionModalStep, isInteractionModalVisible, selectedAction, deviceInteractions, activeSelectedDevice, selectedDevices, isApiFetching, currentStatusText } = useDevicesStateContext();
    const legacyClient = useWebPortalLegacyClient();
    const client = useWebPortalApiClient();

    const handleSignOffIfApplicable = (device: Device) => {
        if (device.type === DeviceTypes.StatStripXpress2) {
            dispatch(SetInteractionModalStepAction.create(6));
        } else {
            dispatch(CloseInteractionModalAction.create());
        }
    }

    const handleSignOffSubmit = async (annotation: string) => {
        dispatch(IsFetchingAction.create());

        const testsInSession = await client.client.getBloodGlucoseTests("tests", `sessionId eq ${activeSelectedSessionId}`);

        for (const test of testsInSession.value) {
            await client.client.patchBloodGlucoseTest({
                isAcceptedByTechnician: true
            }, test.id, annotation);
        }

        dispatch(CloseInteractionModalAction.create());
    }

    const handleClose = () => {
        dispatch(CloseInteractionModalAction.create());
    }

    const renderPage = (page) => {
        switch (page) {
            case 1:
                return (<ActionSelection dispatch={dispatch} selectedOption={selectedAction} options={deviceInteractions} />);
            case 2:
                return (<SessionSelection dispatch={dispatch} />);
            case 3:
                return (<Upload
                    deviceType={activeSelectedDevice ? activeSelectedDevice.type : selectedDevices[0].type}
                    dispatch={dispatch}
                />);
            case 4:
                return (<Success explanationText={currentStatusText} selectedInteraction={selectedAction} devices={selectedDevices} deviceStatuses={deviceStatus} handleNext={() => handleSignOffIfApplicable(activeSelectedDevice)} />);
            case 5:
                return (<Failure explanationText={currentStatusText} handleClose={handleClose} devices={selectedDevices} deviceStatuses={deviceStatus} />);
            case 6:
                return (
                    <AuditModalBody isSubmitting={isApiFetching} onClose={handleClose} onSubmit={(form) => handleSignOffSubmit(form.Annotation)}>
                        <p>Please confirm that the expected number of tests have uploaded to the Web Portal</p>
                    </AuditModalBody>
                );
            case 10:
                return (<MailConfirmation
                    isFetchingAddress={isFetchingAddress}
                    mailAddress={mailAddress}
                    selectedSessionIds={selectedSessionIds}
                    dispatch={dispatch}
                />);
            case 11:
                return (<DeviceUnresponsive onClose={handleClose} />);
            case 20:
                return (<ResetSelection dispatch={dispatch} />);
            case 21:
                return (<DiaryResetSubjectConfiguration dispatch={dispatch} />);
            case 22:
                return (<DiaryLaunchCalibrationVerification handleClose={handleClose} />)
        }
        return (null);
    }

    if (!isInteractionModalVisible) {
        return null;
    } else {
        return (
            <div>
                <div className="modal-backdrop fade in"></div>
                <div id="device-return-wizard" className="modal show bootstrap-dialog">
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <ErrorBoundary>
                                {renderPage(interactionModalStep)}
                            </ErrorBoundary>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}
